import {
    DISABLED_MATTER_TYPES_BY_LAW_FIRM_ID,
    ILA_LAW_FIRM_ID,
    LENNOX_LAW_FIRM_ID,
    MARIE_LAW_FIRM_ID,
    MCGUINTY_LAW_FIRM_ID,
    PETER_CULLEN_LAW_FIRM_ID,
    POTESTIO_LAW_FIRM_ID,
    RAMSAY_LAW_FIRM_ID,
    RITA_LAW_FIRM_ID,
    TITLE_TRANSFER_LAW_FIRM_IDS,
} from '@common/constants';
import {
    ALL_PROSPECT_PRACTICE_AREAS,
    GENERAL_INTAKE_MATTER_TYPES,
    INCORPORATION_MATTER_TYPES,
    LEXO_CIVIL_MATTER_TYPES,
    LEXO_CRIMINAL_MATTER_TYPES,
    LEXO_FAMILY_MATTER_TYPES,
    LEXO_IMMIGRATION_MATTER_TYPES,
    RAMSAY_FAMILY_MATTER_TYPES,
    type ProspectMatterType,
    type ProspectPracticeArea,
    REAL_ESTATE_MATTER_TYPES,
    WILLS_AND_ESTATES_MATTER_TYPES,
} from '@common/enums';
import { getMatterTypeLabel } from '@common/util';

export interface MatterTypeOption {
    label: string;
    value: ProspectMatterType;
    disabled?: boolean;
}

function toMatterTypeOptions(
    matterTypes: readonly ProspectMatterType[],
    disabledValues: readonly ProspectMatterType[] = [],
    lawFirmId?: number,
): MatterTypeOption[] {
    const disabledForLawFirm =
        lawFirmId == null ? [] : (DISABLED_MATTER_TYPES_BY_LAW_FIRM_ID[lawFirmId] ?? []);

    return matterTypes.map((value) => ({
        label: getMatterTypeLabel(value),
        value,
        disabled: disabledValues.includes(value) || disabledForLawFirm.includes(value),
    }));
}

interface GetPracticeAreaMatterTypeOptionsParams {
    lawFirmId?: number;
    isTestFirm?: boolean;
}

export function getPracticeAreaMatterTypeOptions({
    lawFirmId,
    isTestFirm = false,
}: GetPracticeAreaMatterTypeOptionsParams = {}): Record<ProspectPracticeArea, MatterTypeOption[]> {
    const realEstateMatterTypes = REAL_ESTATE_MATTER_TYPES.filter(
        (value) =>
            (value !== 'TitleTransfer' ||
                (lawFirmId != null && TITLE_TRANSFER_LAW_FIRM_IDS.includes(lawFirmId))) &&
            (value !== 'BareTransfer' || lawFirmId === LENNOX_LAW_FIRM_ID) &&
            (value !== 'BareTransferAndRefinance' || lawFirmId === LENNOX_LAW_FIRM_ID) &&
            (value !== 'ILA' || lawFirmId === ILA_LAW_FIRM_ID) &&
            (value !== 'PurchaseAndSale' || lawFirmId === MARIE_LAW_FIRM_ID) &&
            (value !== 'Transfer' || lawFirmId === PETER_CULLEN_LAW_FIRM_ID) &&
            (value !== 'TransferAndRefinance' || lawFirmId === PETER_CULLEN_LAW_FIRM_ID) &&
            (value !== 'TransferOfTitle' || lawFirmId === MCGUINTY_LAW_FIRM_ID) &&
            (value !== 'SurvivorshipApplication' || lawFirmId === MCGUINTY_LAW_FIRM_ID),
    );

    const willsAndEstatesMatterTypes = WILLS_AND_ESTATES_MATTER_TYPES.filter(
        (value) => value !== 'PowerOfAttorney' || lawFirmId === RITA_LAW_FIRM_ID,
    );

    const familyMatterTypes: ProspectMatterType[] = [
        'Family',
        ...(isTestFirm ? LEXO_FAMILY_MATTER_TYPES : []),
        ...(lawFirmId === PETER_CULLEN_LAW_FIRM_ID ? (['SeparationAndDivorce'] as const) : []),
        ...(lawFirmId === RAMSAY_LAW_FIRM_ID ? RAMSAY_FAMILY_MATTER_TYPES : []),
    ];

    const immigrationMatterTypes: ProspectMatterType[] = [
        'Immigration',
        ...(isTestFirm ? LEXO_IMMIGRATION_MATTER_TYPES : []),
    ];

    const civilMatterTypes: ProspectMatterType[] =
        lawFirmId === MCGUINTY_LAW_FIRM_ID
            ? ['Litigation']
            : ['Civil', ...(isTestFirm ? LEXO_CIVIL_MATTER_TYPES : [])];

    const criminalMatterTypes: ProspectMatterType[] = [
        'Criminal',
        ...(isTestFirm ? LEXO_CRIMINAL_MATTER_TYPES : []),
    ];

    const incorporationMatterTypes = INCORPORATION_MATTER_TYPES.filter(
        (value) =>
            value !== 'Corporate' ||
            lawFirmId === POTESTIO_LAW_FIRM_ID ||
            lawFirmId === MCGUINTY_LAW_FIRM_ID,
    );

    return {
        RealEstate: toMatterTypeOptions(realEstateMatterTypes, [], lawFirmId),
        Incorporation: toMatterTypeOptions(
            incorporationMatterTypes,
            ['NotForProfitIncorporation'],
            lawFirmId,
        ),
        WillsAndEstates: toMatterTypeOptions(willsAndEstatesMatterTypes, [], lawFirmId),
        Civil: toMatterTypeOptions(civilMatterTypes, [], lawFirmId),
        Family: toMatterTypeOptions(familyMatterTypes, [], lawFirmId),
        Criminal: toMatterTypeOptions(criminalMatterTypes, [], lawFirmId),
        Immigration: toMatterTypeOptions(immigrationMatterTypes, [], lawFirmId),
        GeneralIntake: toMatterTypeOptions(GENERAL_INTAKE_MATTER_TYPES, [], lawFirmId),
    };
}

export { ALL_PROSPECT_PRACTICE_AREAS };
